import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, App, Button, Card, Popconfirm, Space, Switch, Table, Tag, Typography } from 'antd';
import { del, get, put } from '../../../api/client';

interface BackfillItem {
  userId: string;
  username: string | null;
  expiresAt: string;
  remainingMs: number;
}

interface BackfillView {
  locked: boolean;
  items: BackfillItem[];
}

function remainingOf(ms: number): string {
  const totalMinutes = Math.max(0, Math.floor(ms / 60000));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours <= 0) return `${minutes} 分钟`;
  return minutes > 0 ? `${hours} 小时 ${minutes} 分钟` : `${hours} 小时`;
}

/**
 * 补录策略面板。
 *
 * 补录窗口现在完全在服务端（`settings` 表按用户存到期时间，读时与当前时间比对），
 * 这里的熔断是唯一的「一刀切关闸」手段：置位后所有用户即使自己开着也提交不了过去日期，
 * 已经开着的窗口不用逐个清——`getOrderBehavior` 会直接报 lockedByAdmin。
 */
export function BackfillPolicyPanel() {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ['admin-backfill'],
    queryFn: () => get<BackfillView>('/admin/backfill'),
  });

  const invalidate = () => void qc.invalidateQueries({ queryKey: ['admin-backfill'] });

  const lock = useMutation({
    mutationFn: (locked: boolean) => put('/admin/backfill/policy', { locked }),
    onSuccess: (_data, locked) => {
      message.success(locked ? '已关闭全平台补录' : '已放开全平台补录');
      invalidate();
    },
    onError: (e) => message.error(e instanceof Error ? e.message : '操作失败'),
  });

  const forceClose = useMutation({
    mutationFn: (userId: string) => del(`/admin/backfill/${userId}`),
    onSuccess: () => {
      message.success('已强制关闭该用户的补录窗口');
      invalidate();
    },
    onError: (e) => message.error(e instanceof Error ? e.message : '操作失败'),
  });

  const locked = query.data?.locked ?? false;
  const items = query.data?.items ?? [];

  return (
    <Card
      title="补录模式策略"
      extra={
        <Space>
          <Typography.Text type="secondary">全平台禁止补录（熔断）</Typography.Text>
          <Switch
            checked={locked}
            loading={lock.isPending}
            checkedChildren="已熔断"
            unCheckedChildren="正常"
            onChange={(checked) => lock.mutate(checked)}
          />
        </Space>
      }
      style={{ marginBottom: 16 }}
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="补录闸门说明"
        description="正常订单均为未来日期的预约。用户在「设置 - 订单设置」开启补录模式后可提交过去的预约日期，窗口开启 24 小时后自动关闭，防止遗忘关闭造成误录。闸门由服务端在下单与改档期时判定，四端一致；熔断置位后该判定对所有用户直接拒绝。历史订单的补录标记同样由服务端下发（订单列表/详情的 isBackfilled），各端只负责展示。"
      />
      {locked && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="熔断已生效"
          description="当前全平台都不能提交过去的预约日期；解除后，仍在各自窗口内的用户会自动恢复可用。"
        />
      )}
      <Table<BackfillItem>
        size="small"
        rowKey="userId"
        loading={query.isLoading}
        dataSource={items}
        pagination={false}
        scroll={{ x: 560 }}
        locale={{ emptyText: '当前没有用户处于补录窗口内' }}
        columns={[
          {
            title: '用户',
            dataIndex: 'username',
            width: 160,
            render: (username: string | null) => username || <Typography.Text type="secondary">（账号已注销）</Typography.Text>,
          },
          {
            title: '窗口到期时间',
            dataIndex: 'expiresAt',
            width: 180,
            render: (value: string) => new Date(value).toLocaleString(),
          },
          {
            title: '剩余',
            dataIndex: 'remainingMs',
            width: 100,
            render: (ms: number) => <Tag color="processing">{remainingOf(ms)}</Tag>,
          },
          {
            title: '操作',
            key: 'actions',
            width: 120,
            fixed: 'right',
            render: (_: unknown, record) => (
              <Popconfirm
                title="强制关闭该用户的补录窗口？"
                description="该用户随即无法提交过去的预约日期，需要时可自行重新开启"
                onConfirm={() => forceClose.mutate(record.userId)}
              >
                <Button size="small" danger type="link">
                  强制关闭
                </Button>
              </Popconfirm>
            ),
          },
        ]}
      />
    </Card>
  );
}
